import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  FaSearch,
  FaTicketAlt,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaCalendarAlt,
  FaExclamationCircle,
  FaComments,
  FaPaperPlane,
  FaPaperclip,
  FaEye,
  FaTrash,
  FaShareSquare,
} from "react-icons/fa";
import axios from "axios";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import Button from "../../components/general/button/Button";
import Pagination from "../../components/general/pagination/Pagination";
import StatCard from "../../components/admin/statcard/StatCard";
import { HelpPageSkeleton } from "../../components/general/skeleton/SkeletonBlock";
import Swal from "sweetalert2";
import "../../assets/css/customer/customerDashboard/help/Help.css";
import MyContext from "../../context/MyContext";
import { getEcho } from "../../components/general/echo/Echo";

const Help = () => {
  const [against, setAgainst] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { baseUrl, authToken } = useContext(MyContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [chatMessage, setChatMessage] = useState("");
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [orderError, setOrderError] = useState("");

  // Refund bank details — jab seller/deliveryman ne fault maan liya
  // (refund_due = 1), customer yahan apni bank details deta hai
  const [refundBankName, setRefundBankName] = useState("");
  const [refundAccountTitle, setRefundAccountTitle] = useState("");
  const [refundAccountNumber, setRefundAccountNumber] = useState("");

  // Ticket creation — claimed amount + evidence image, taake admin
  // refund process karte waqt customer ka claim aur proof dekh sake
  const [claimedAmountDifference, setClaimedAmountDifference] = useState("");
  const [ticketAttachmentImage, setTicketAttachmentImage] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isFormValid =
    selectedOrderId && against && subject.trim() && type && description.trim();

  // Common auth header
  const authHeaders = {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${authToken}`,
  };

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationMessages]);

  // viewAllFaqs — public endpoint, no customer auth needed
  const viewAllFaqs = () => {
    return axios
      .get(`${baseUrl}/customer/faq/viewAllFaqs`, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setFaqs(response.data.faqs);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "FAQS cannot load" });
      });
  };

  // addTicket
  const addTicket = () => {
    const formData = new FormData();
    const selectedOrder = customerOrders.find(
      (order) => order.id == selectedOrderId,
    );
    if (!selectedOrder) {
      setOrderError("Please select an order.");
      return;
    }

    setOrderError("");
    formData.append("subject", subject);
    formData.append("type", type);
    formData.append("description", description);
    formData.append("kmorder_id", selectedOrderId);
    if (claimedAmountDifference) {
      formData.append("claimed_amount_difference", claimedAmountDifference);
    }
    if (ticketAttachmentImage) {
      formData.append("attachment_image", ticketAttachmentImage);
    }
    if (against === "seller") {
      formData.append("kmseller_id", selectedOrder.kmseller_id);
    } else {
      formData.append("kmdeliveryman_id", selectedOrder.kmdeliveryman_id);
    }
    axios
      .post(`${baseUrl}/customer/help/addTicket`, formData, {
        headers: authHeaders,
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Support Ticket added successfully!",
          });
          viewAllTickets();
          setSubject("");
          setType("");
          setDescription("");
          setSelectedOrderId("");
          setClaimedAmountDifference("");
          setTicketAttachmentImage(null);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to add Support Ticket!",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.status === 401) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add Support Ticket!",
        });
      });
  };

  // viewAllTickets
  const viewAllTickets = () => {
    return axios
      .get(`${baseUrl}/customer/help/viewAllTickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setTickets(response.data.ticket);
        }
      })
      .catch((error) => {
        console.log(error);
        // 401 = token expire, 403 = token kisi aur role (seller/admin) ka hai
        if ([401, 403].includes(error.response?.status)) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Support Tickets cannot load",
        });
      });
  };

  // viewAllOrders
  const viewAllOrders = () => {
    return axios
      .get(`${baseUrl}/customer/order/viewAllOrders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setCustomerOrders(response.data.orders);
        }
      })
      .catch((error) => {
        console.log(error);
        if ([401, 403].includes(error.response?.status)) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Orders cannot load",
        });
      });
  };

  // ── Initial load: wait for FAQs + Tickets + Orders before hiding skeleton ──
  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/customer/login");
      return;
    }

    setLoading(true);
    let completedCalls = 0;
    const markCallDone = () => {
      completedCalls++;
      if (completedCalls === 3) setLoading(false);
    };
    viewAllFaqs().then(markCallDone).catch(markCallDone);
    viewAllTickets().then(markCallDone).catch(markCallDone);
    viewAllOrders().then(markCallDone).catch(markCallDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // deleteTicket
  const deleteTicket = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This ticket will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(`${baseUrl}/customer/help/deleteTicket`, formData, {
            headers: authHeaders,
          })
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "ticket deleted successfully!",
              });
              viewAllTickets();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete ticket.",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            if (error.response?.status === 401) {
              navigate("/customer/login");
              return;
            }
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete ticket.",
            });
          });
      }
    });
  };

  // fetchChatForHelp
  const fetchChatForHelp = (ticket) => {
    const formData = new FormData();
    formData.append("kmhelpandsupport_id", ticket.id);

    axios
      .post(`${baseUrl}/customer/chats/fetchChatForHelp`, formData, {
        headers: authHeaders,
      })
      .then((res) => {
        if (res.data.success) {
          setConversationMessages(res.data.chats);
          setConversationId(res.data.conversation_id);
        } else {
          setConversationMessages([]);
        }
      })
      .catch((err) => {
        console.log(err);
        if (err.response?.status === 401) {
          navigate("/customer/login");
        }
      });
  };

  // ── real-time — help-support conversation ke channel pe naya
  // message aate hi list mein append kar do (CustomerMessages.js
  // jaisa pattern; backend wahi sendMessage/NewChatMessage event
  // use karta hai jo general chat mein bhi use hota hai) ──
  useEffect(() => {
    if (!authToken || !conversationId) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `conversation.${conversationId}`;
    const channel = echo.private(channelName);

    channel.listen(".new-message", (e) => {
      const incoming = e.message;
      setConversationMessages((prev) => {
        if (prev.some((m) => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, conversationId]);

  // Forward to Admin
  const forwardToAdmin = (id) => {
    const formData = new FormData();
    formData.append("id", id);
    axios
      .post(`${baseUrl}/customer/help/forwardToAdmin`, formData, {
        headers: authHeaders,
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Ticket forwarded to admin successfully!",
          });
          viewAllTickets();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to forward ticket.",
          });
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to forward ticket.",
        });
      });
  };

  // Submit Refund Bank Details
  const submitRefundBankDetails = () => {
    if (!refundBankName || !refundAccountTitle || !refundAccountNumber) {
      Swal.fire({
        icon: "warning",
        title: "Missing details",
        text: "Please fill in all bank detail fields.",
      });
      return;
    }
    const formData = new FormData();
    formData.append("id", selectedTicket.id);
    formData.append("refund_bank_name", refundBankName);
    formData.append("refund_account_title", refundAccountTitle);
    formData.append("refund_account_number", refundAccountNumber);
    axios
      .post(`${baseUrl}/customer/help/submitRefundBankDetails`, formData, {
        headers: authHeaders,
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data.message,
          });
          setRefundBankName("");
          setRefundAccountTitle("");
          setRefundAccountNumber("");
          viewAllTickets();
          setShowViewModal(false);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message || "Failed to submit bank details.",
          });
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to submit bank details.",
        });
      });
  };

  // sendMessage
  const sendMessage = () => {
    if (!chatMessage.trim() && !attachedFile) return;
    if (!conversationId) return;
    const formData = new FormData();
    formData.append("kmconversation_id", conversationId);
    formData.append("send_by", "customer");
    formData.append("message", chatMessage);
    formData.append("image", attachedFile);

    axios
      .post(`${baseUrl}/customer/chat/sendMessage`, formData, {
        headers: authHeaders,
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setChatMessage("");
          setAttachedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          fetchChatForHelp(selectedTicket);
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          navigate("/customer/login");
        }
      });
  };

  // Format timestamp
  const formatTime = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    const state = location.state;
    if (state?.autoFill) {
      setSubject(state.subject || "");
      setType(state.type || "");
      if (state.orderId) {
        setSelectedOrderId(String(state.orderId));
      }
    }
  }, [location.state]);

  // Stats
  const statsData = [
    {
      icon: FaTicketAlt,
      label: "Total Tickets",
      badge: "All",
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      value: (tickets || []).length.toString(),
    },
    {
      icon: FaClock,
      label: "Open Tickets",
      badge: "Pending",
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      iconColor: "#ff9800",
      value: (tickets || [])
        .filter((t) => t.status === "open")
        .length.toString(),
    },
    {
      icon: FaExclamationCircle,
      label: "In Progress",
      badge: "Active",
      iconBg: "linear-gradient(135deg, #ffebee, #ffcdd2)",
      iconColor: "#f44336",
      value: (tickets || [])
        .filter((t) => t.status === "in_progress")
        .length.toString(),
    },
    {
      icon: FaCheckCircle,
      label: "Resolved",
      badge: "Done",
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      value: (tickets || [])
        .filter((t) => t.status === "resolved")
        .length.toString(),
    },
  ];

  const getStatusBadgeClass = (s) =>
    ({
      open: "status-open",
      in_progress: "status-in-progress",
      resolved: "status-resolved",
      closed: "status-closed",
    })[s] || "status-open";

  const getStatusLabel = (s) =>
    ({
      open: "Open",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    })[s] || s;

  const TicketActionDropdown = ({ ticket, onView }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
      <div className="help-action-wrapper" ref={ref}>
        <button
          className="help-action-toggle"
          onClick={() => setOpen((p) => !p)}
          aria-label="Actions"
        >
          <span className="help-action-dot" />
          <span className="help-action-dot" />
          <span className="help-action-dot" />
        </button>

        {open && (
          <div className="help-action-dropdown">
            <button
              className="help-action-item"
              onClick={() => {
                onView(ticket);
                setOpen(false);
                fetchChatForHelp(ticket);
              }}
            >
              <FaEye className="help-action-icon" />
              View Details
            </button>

            {/* ── CHANGE 1: Forward to Admin sirf tab dikhao jab
                is_forwarded_to_admin != 1 ho ── */}
            {Number(ticket.forwarded_to_admin) !== 1 && (
              <button
                className="help-action-item"
                onClick={() => {
                  forwardToAdmin(ticket.id);
                  setOpen(false);
                }}
              >
                <FaShareSquare className="help-action-icon" />
                Forward to Admin
              </button>
            )}

            {/* ── CHANGE 2: Delete sirf tab dikhao jab
                ticket closed ho ── */}
            {ticket.status === "closed" && (
              <button
                className="help-action-item help-action-danger"
                onClick={() => {
                  deleteTicket(ticket.id);
                  setOpen(false);
                }}
              >
                <FaTrash className="help-action-icon" />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredTickets = (tickets || []).filter((t) => {
    const ms = filterStatus === "all" || t.status === filterStatus;
    const mq =
      t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.type.toLowerCase().includes(searchTerm.toLowerCase());
    return ms && mq;
  });

  const currentTickets = filteredTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const ticketTypes = [
    "General Inquiry",
    "Order Issue",
    "Payment Problem",
    "Product Question",
    "Technical Support",
    "Account Help",
    "Other",
  ];

  if (loading)
    return (
      <CustomerMasterLayout>
        <HelpPageSkeleton />
      </CustomerMasterLayout>
    );

  return (
    <CustomerMasterLayout>
      <Container fluid className="help-container">
        {/* Stats */}
        <Row className="g-3 mb-4">
          {statsData.map((stat, i) => (
            <Col key={i} xs={12} sm={6} lg={6} xl={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>
        {/* Ticket Table */}
        <Row>
          <Col xs={12}>
            <div className="help-filters-bar mt-3 mb-3">
              <div className="help-search-box">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <select
                className="help-filter-select"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="help-table-wrapper">
              <table className="ksa-support-table">
                <thead>
                  <tr>
                    <th>id</th>
                    <th>Subject</th>
                    <th>Type</th>
                    <th>Order</th>
                    <th>Against</th>
                    <th>Date</th>
                    <th>Forwarded</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTickets.length > 0 ? (
                    currentTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="ksa-support-ticket-id">{ticket.id}</td>
                        <td className="ksa-support-subject">
                          {ticket.subject}
                        </td>
                        <td className="ksa-support-category">{ticket.type}</td>
                        <td>
                          <span
                            style={{
                              color: "#198754",
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontWeight: "600",
                            }}
                            onClick={() =>
                              navigate("/customer/order-details", {
                                state: { orderId: ticket?.order_id },
                              })
                            }
                          >
                            #{ticket.kmorder?.id}
                          </span>
                        </td>
                        <td>
                          {ticket.kmdeliveryman_id ? (
                            <>
                              <p style={{ margin: 0, fontWeight: "600" }}>
                                {ticket.kmorder?.kmdeliveryman?.name}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#777",
                                }}
                              >
                                {ticket.kmorder?.kmdeliveryman?.email || "N/A"}
                              </p>
                            </>
                          ) : (
                            <>
                              <p style={{ margin: 0, fontWeight: "600" }}>
                                {ticket.kmorder?.kmseller?.shop_name}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#777",
                                }}
                              >
                                {ticket.kmorder?.kmseller?.email || "N/A"}
                              </p>
                            </>
                          )}
                        </td>{" "}
                        <td className="ksa-support-date">
                          {(ticket.created_at || ticket.date)?.split("T")[0]}
                        </td>
                        <td>
                          {Number(ticket.forwarded_to_admin) === 1 ? (
                            <span
                              style={{
                                color: "#198754",
                                fontWeight: "600",
                              }}
                            >
                              Yes
                            </span>
                          ) : (
                            <span
                              style={{
                                color: "#6c757d",
                              }}
                            >
                              No
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`ksa-support-status-badge ${getStatusBadgeClass(ticket.status)}`}
                          >
                            {getStatusLabel(ticket.status)}
                          </span>
                        </td>
                        <td>
                          <TicketActionDropdown
                            ticket={ticket}
                            onView={(ticket) => {
                              setSelectedTicket(ticket);
                              setShowViewModal(true);
                            }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="empty-state">
                        No tickets found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={filteredTickets.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(v) => {
                setItemsPerPage(v);
                setCurrentPage(1);
              }}
            />
          </Col>
        </Row>

        <Row className="g-4">
          {/* Left: Submit Ticket Form */}
          <Col xs={12} lg={6}>
            <div className="help-form-card">
              <h4 className="help-form-title">
                <FaTicketAlt className="me-2" /> Submit a Ticket
              </h4>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!subject || !type || !description) {
                    Swal.fire({
                      title: "Error!",
                      text: "Please fill all fields.",
                      icon: "error",
                    });
                    return;
                  }
                  if (!authToken) {
                    Swal.fire({
                      title: "Error!",
                      text: "Please login again.",
                      icon: "error",
                    });
                    navigate("/customer/login");
                    return;
                  }
                  addTicket();
                }}
              >
                <div className="help-form-group">
                  <label>Select Order</label>
                  <select
                    className="help-form-select"
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(e.target.value)}
                  >
                    <option value="">-- Select an order --</option>
                    {(customerOrders || []).map((order) => (
                      <option key={order.id} value={order.id}>
                        Order #{order.id} - {order.kmseller?.shop_name || "N/A"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="help-form-group">
                  <label>Against</label>

                  <select
                    className="help-form-select"
                    value={against}
                    onChange={(e) => setAgainst(e.target.value)}
                  >
                    <option value="">-- Select against--</option>

                    <option value="seller">Seller</option>
                    <option value="deliveryman">Deliveryman</option>
                  </select>
                </div>
                <div className="help-form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    className="help-form-input"
                    placeholder="Brief description of your issue"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
                <div className="help-form-group">
                  <label>Issue Type *</label>
                  <select
                    className="help-form-select"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="">Select type...</option>
                    {ticketTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="help-form-group">
                  <label>Description *</label>
                  <textarea
                    className="help-form-textarea"
                    rows={5}
                    placeholder="Describe your issue in detail..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="help-form-group">
                  <label>Claimed Refund Amount (optional)</label>
                  <input
                    type="number"
                    min="0"
                    className="help-form-input"
                    placeholder="e.g. 500"
                    value={claimedAmountDifference}
                    onChange={(e) =>
                      setClaimedAmountDifference(e.target.value)
                    }
                  />
                </div>

                <div className="help-form-group">
                  <label>Attach Evidence (optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="help-form-input"
                    onChange={(e) =>
                      setTicketAttachmentImage(e.target.files[0] || null)
                    }
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  icon={<FaPaperPlane size={14} />}
                  disabled={!isFormValid}
                >
                  Submit Ticket
                </Button>

                {!isFormValid && (
                  <p
                    style={{
                      color: "#dc3545",
                      fontSize: "13px",
                      marginTop: "8px",
                      textAlign: "center",
                    }}
                  ></p>
                )}
              </form>
            </div>
          </Col>

          {/* Right: FAQ */}
          <Col xs={12} lg={6}>
            <div className="help-faq-card">
              <h4 className="help-faq-title">
                <FaComments className="me-2" /> Frequently Asked Questions
              </h4>
              {(faqs || []).map((faq) => (
                <div key={faq.id} className="help-faq-item">
                  <button
                    className="help-faq-question"
                    onClick={() =>
                      setExpandedFaq(expandedFaq === faq.id ? null : faq.id)
                    }
                  >
                    <span>{faq.question}</span>
                    {expandedFaq === faq.id ? (
                      <FiChevronUp size={16} />
                    ) : (
                      <FiChevronDown size={16} />
                    )}
                  </button>
                  {expandedFaq === faq.id && (
                    <p className="help-faq-answer">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>

        
      </Container>

      {/* View Ticket Modal */}
      {showViewModal && selectedTicket && (
        <div
          className="ksa-modal-overlay"
          onClick={() => setShowViewModal(false)}
        >
          <div
            className="ksa-modal ksa-modal-lg ksa-support-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ksa-support-modal-header">
              <div>
                <h3 className="ksa-support-modal-title">Ticket Details</h3>
                <p className="ksa-support-modal-number">{selectedTicket.id}</p>
              </div>
              <button
                className="ksa-modal-close"
                onClick={() => setShowViewModal(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-support-modal-body">
              <Row className="g-4">
                {/* Left — Info */}
                <Col xs={12} md={4}>
                  <div className="ksa-support-info-card">
                    <h5>Ticket Information</h5>
                    <div className="ksa-support-info-item">
                      <FaTicketAlt />
                      <div>
                        <label>Subject</label>
                        <p>{selectedTicket.subject}</p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaCalendarAlt />
                      <div>
                        <label>Created</label>
                        <p>
                          {new Date(
                            selectedTicket.created_at || selectedTicket.date,
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaComments />
                      <div>
                        <label>Description</label>
                        <p>{selectedTicket.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ksa-support-status-card">
                    <h5>Status</h5>
                    <div className="ksa-support-badges">
                      <span
                        className={`ksa-support-status-badge ${getStatusBadgeClass(selectedTicket.status)}`}
                      >
                        {getStatusLabel(selectedTicket.status)}
                      </span>
                    </div>
                  </div>

                  {/* Teacher ka kaha hua flow: seller/deliveryman ne
                      fault maan liya (refund_due = 1) — customer apni
                      bank details de taake order "Refund Pending" ho
                      jaye aur admin refund process kar sake */}
                  {selectedTicket.refund_due == 1 &&
                    selectedTicket.kmorder?.paymentStatus !== "Refund Pending" &&
                    selectedTicket.kmorder?.paymentStatus !== "Refunded" && (
                      <div className="ksa-support-info-card">
                        <h5>Refund Approved</h5>
                        <p style={{ fontSize: "13px" }}>
                          Your dispute has been resolved in your favor.
                          Please submit your bank details to receive the
                          refund.
                        </p>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Bank Name"
                          value={refundBankName}
                          onChange={(e) => setRefundBankName(e.target.value)}
                          style={{ marginBottom: "8px" }}
                        />
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Account Title"
                          value={refundAccountTitle}
                          onChange={(e) =>
                            setRefundAccountTitle(e.target.value)
                          }
                          style={{ marginBottom: "8px" }}
                        />
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Account Number"
                          value={refundAccountNumber}
                          onChange={(e) =>
                            setRefundAccountNumber(e.target.value)
                          }
                          style={{ marginBottom: "8px" }}
                        />
                        <Button
                          variant="primary"
                          size="small"
                          onClick={submitRefundBankDetails}
                        >
                          Submit Bank Details
                        </Button>
                      </div>
                    )}

                  {selectedTicket.refund_due == 1 &&
                    (selectedTicket.kmorder?.paymentStatus ===
                      "Refund Pending" ||
                      selectedTicket.kmorder?.paymentStatus ===
                        "Refunded") && (
                      <div className="ksa-support-info-card">
                        <p style={{ fontSize: "13px", margin: 0 }}>
                          {selectedTicket.kmorder?.paymentStatus ===
                          "Refunded"
                            ? "Your refund has been processed."
                            : "Your bank details have been submitted — your refund is being processed."}
                        </p>
                      </div>
                    )}
                </Col>

                {/* Right — Chat */}
                <Col xs={12} md={8}>
                  <div className="ksa-support-chat-section">
                    <h5 className="ksa-support-chat-title">
                      <FaComments /> Conversation
                    </h5>
                    <div className="ksa-support-subject-banner">
                      <strong>Type:</strong> {selectedTicket.type}
                    </div>

                    {/* Messages */}
                    <div className="ksa-support-chat-container">
                      {conversationMessages?.map((msg) => (
                        <div
                          key={msg.id}
                          className={`ksa-support-chat-message ${
                            msg.send_by === "customer" ? "user" : "other"
                          }`}
                        >
                          <div className="ksa-support-chat-bubble">
                            {msg.send_by === "seller" && (
                              <small
                                style={{
                                  fontWeight: "600",
                                  color: "#666",
                                  display: "block",
                                  marginBottom: "5px",
                                }}
                              >
                                {selectedTicket?.kmorder?.kmseller?.shop_name}
                              </small>
                            )}
                            {msg.send_by === "admin" && (
                              <small
                                style={{
                                  fontWeight: "600",
                                  color: "#666",
                                  display: "block",
                                  marginBottom: "5px",
                                }}
                              >
                                Admin
                              </small>
                            )}
                            {msg.image && (
                              <img
                                src={`${baseUrl}/storage/${msg.image}`}
                                alt="attachment"
                                style={{
                                  maxWidth: "200px",
                                  borderRadius: "8px",
                                  marginBottom: "4px",
                                }}
                              />
                            )}
                            {msg.message && <p>{msg.message}</p>}
                            <span className="ksa-support-chat-time">
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    {selectedTicket.status !== "resolved" &&
                    selectedTicket.status !== "closed" ? (
                      <div className="ksa-support-chat-input-wrapper">
                        {attachedFile && (
                          <div className="ksa-support-attached-file">
                            <FaPaperclip />
                            <span>{attachedFile.name}</span>
                            <button
                              onClick={() => {
                                setAttachedFile(null);
                                if (fileInputRef.current)
                                  fileInputRef.current.value = "";
                              }}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        )}
                        <div className="ksa-support-chat-input-container">
                          <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={(e) =>
                              setAttachedFile(e.target.files[0] || null)
                            }
                            accept="image/*,.pdf,.doc,.docx"
                          />
                          <label
                            className="ksa-support-attach-btn"
                            onClick={() => fileInputRef.current.click()}
                          >
                            <FaPaperclip />
                          </label>
                          <input
                            type="text"
                            className="ksa-support-chat-input"
                            placeholder="Type your message..."
                            value={chatMessage}
                            onChange={(e) => setChatMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                            }}
                          />
                          <button
                            className="ksa-support-chat-send-btn"
                            onClick={sendMessage}
                            disabled={!chatMessage.trim() && !attachedFile}
                          >
                            <FaPaperPlane />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ksa-support-ticket-resolved">
                        <FaCheckCircle /> Ticket{" "}
                        {selectedTicket.status === "resolved"
                          ? "Resolved"
                          : "Closed"}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            <div className="ksa-support-modal-footer">
              <Button
                variant="secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </CustomerMasterLayout>
  );
};

export default Help;
